/** Dashboard layout with sidebar navigation and top bar. */
import React, { ReactNode, useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  Container,
  CssBaseline,
  IconButton,
  Chip,
} from "@mui/material";
import {
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { clearToken, authApi } from "../../api/client";
import GroupedNav from "../navigation/GroupedNav";
import LiveIndicator from "../navigation/LiveIndicator";
import Breadcrumbs from "../navigation/Breadcrumbs";
import ConfirmDialog from "../common/ConfirmDialog";
import CommandPalette from "../common/CommandPalette";
import { toggleThemeMode } from "../../App";
import { RefreshProvider, useRefresh } from "./RefreshContext";

const DRAWER_WIDTH = 280; // Increased width for grouped nav

/** App version surfaced in the toolbar (G3) — bump on releases. */
const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

/** B2 fallback: when the deployed backend predates /auth/me it 404s.
 * Remember that per browser session so we don't spam 404s on every layout
 * mount (legacy localStorage auth still works until the backend catches up). */
function authMeProbeAllowed(): boolean {
  try {
    return sessionStorage.getItem("auth_me_unavailable") !== "1";
  } catch {
    return true;
  }
}

function markAuthMeUnavailable(): void {
  try {
    sessionStorage.setItem("auth_me_unavailable", "1");
  } catch {
    /* private mode — nothing we can do */
  }
}

interface DashboardLayoutProps {
  children: ReactNode;
}

/** Inner layout — consumes the refresh context (needs to be inside provider). */
function DashboardLayoutInner({ children }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { autoRefresh, toggleAutoRefresh, triggerRefresh, refreshing } = useRefresh();
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [themeMode, setThemeMode] = useState<"light" | "dark">(
    () => (localStorage.getItem("theme_mode") as "light" | "dark") || "light"
  );

  // A3: ⌘K / Ctrl+K opens the command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // B2: validate the session on boot instead of trusting the localStorage
  // flag — a stale flag previously kept "logged-in" chrome alive until the
  // first API call hard-redirected mid-task. Skipped entirely when the
  // deployed backend doesn't have /auth/me yet (404 → legacy behavior).
  useEffect(() => {
    if (!authMeProbeAllowed()) return;
    let cancelled = false;
    authApi
      .me()
      .then((res) => {
        if (!cancelled && res.data?.authenticated) {
          localStorage.setItem("isAuthenticated", "true");
          // RBAC (staff roles): keep the nav-filtering role fresh.
          if (res.data?.role) {
            localStorage.setItem("auth_role", res.data.role);
          }
        }
      })
      .catch((err) => {
        if (cancelled) return;
        // Only bounce on a real auth rejection (401). Network errors or a
        // missing /me endpoint (older backend) fall back to legacy behavior.
        if (err?.response?.status === 401) {
          clearToken();
          navigate("/login?expired=1", { replace: true });
        } else if (err?.response?.status === 404) {
          markAuthMeUnavailable();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // Keep the dark-mode icon in sync with persisted changes
  useEffect(() => {
    const handler = () =>
      setThemeMode((localStorage.getItem("theme_mode") as "light" | "dark") || "light");
    window.addEventListener("theme-mode-changed", handler);
    return () => window.removeEventListener("theme-mode-changed", handler);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  /** B5: server-side logout with confirmation. */
  const handleLogoutConfirmed = async () => {
    setLoggingOut(true);
    try {
      await authApi.logout();
    } catch {
      // Cookie cleanup happens server-side best-effort; proceed regardless.
    }
    clearToken();
    setLoggingOut(false);
    setLogoutOpen(false);
    navigate("/login");
  };

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: "bold" }}>
          Softswitch
        </Typography>
      </Toolbar>
      <GroupedNav />
    </div>
  );

  const isProduction = process.env.NODE_ENV === "production";

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          ml: { sm: `${DRAWER_WIDTH}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open navigation menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: "none" } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Control Plane
          </Typography>
          {/* G3: environment badge so ops never mistake prod for staging */}
          <Chip
            label={isProduction ? `PROD · ${APP_VERSION}` : `DEV · ${APP_VERSION}`}
            size="small"
            color={isProduction ? "error" : "default"}
            variant="outlined"
            sx={{
              mr: 2,
              color: "inherit",
              borderColor: "rgba(255,255,255,0.4)",
              display: { xs: "none", sm: "inline-flex" },
            }}
            aria-label={`Environment: ${isProduction ? "production" : "development"}`}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <LiveIndicator
              autoRefresh={autoRefresh}
              onToggleRefresh={toggleAutoRefresh}
              onManualRefresh={triggerRefresh}
              refreshing={refreshing}
              interval={30}
            />
            <IconButton
              color="inherit"
              aria-label="Open command palette (Ctrl+K)"
              title="Search (Ctrl+K)"
              onClick={() => setPaletteOpen(true)}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            <IconButton
              color="inherit"
              aria-label="Toggle dark mode"
              onClick={() => toggleThemeMode()}
            >
              {themeMode === "dark" ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => setLogoutOpen(true)}
              title="Logout"
              aria-label="Logout"
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          minHeight: "100vh",
          backgroundColor: "background.default",
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Breadcrumbs />
          {children}
        </Container>
      </Box>

      {/* B5: logout confirmation */}
      <ConfirmDialog
        open={logoutOpen}
        title="Sign out?"
        message="You will be returned to the login screen. Any unsaved changes will be lost."
        confirmLabel="Sign out"
        onClose={() => setLogoutOpen(false)}
        onConfirm={handleLogoutConfirmed}
        loading={loggingOut}
      />

      {/* A3: command palette */}
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        onToggleTheme={toggleThemeMode}
      />
    </Box>
  );
}

/** Main dashboard layout component with sidebar and top bar. */
export default function DashboardLayout({ children }: DashboardLayoutProps) {
  return (
    <RefreshProvider>
      <DashboardLayoutInner>{children}</DashboardLayoutInner>
    </RefreshProvider>
  );
}
